/**
 * FilterSidebar Organism Component
 * Sidebar with multiple filter groups for car search
 */

'use client'

import { FilterGroup, type FilterOption } from '@/components/molecules'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useFilterStore } from '@/store'

interface FilterSidebarProps {
	className?: string
}

const categoryOptions: FilterOption[] = [
	{ value: 'pickup', label: 'PickUp' },
	{ value: 'suv', label: 'Suv Full Size' },
	{ value: 'economy', label: 'Económicos' },
	{ value: 'compact', label: 'Compactos' },
	{ value: 'van', label: 'Monovolumen Grande' },
	{ value: 'midsize', label: 'Mediano' },
	{ value: 'fullsize', label: 'Grande' },
	{ value: 'luxury', label: 'Lujo' },
]

const transmissionOptions: FilterOption[] = [
	{ value: 'automatic', label: 'Automático' },
	{ value: 'manual', label: 'Manual' },
]

const fuelTypeOptions: FilterOption[] = [
	{ value: 'gasoline', label: 'Gasolina' },
	{ value: 'diesel', label: 'Diésel' },
	{ value: 'electric', label: 'Eléctrico' },
	{ value: 'hybrid', label: 'Híbrido' },
]

const seatsOptions: FilterOption[] = [
	{ value: '2', label: '2 asientos' },
	{ value: '4', label: '4 asientos' },
	{ value: '5', label: '5 asientos' },
	{ value: '7', label: '7 asientos' },
	{ value: '9', label: '9+ asientos' },
]

export function FilterSidebar({ className }: FilterSidebarProps) {
	const { filters, updateFilter, resetFilters } = useFilterStore()

	return (
		<div className={cn('space-y-6 p-6 bg-white rounded-lg border', className)}>
			{/* Header */}
			<div className='flex items-center justify-between'>
				<h2 className='text-xl font-semibold text-[#09194D]'>Filtros</h2>
				<Button
					variant='ghost'
					size='sm'
					onClick={resetFilters}
					className='text-sm cursor-pointer'
				>
					Limpiar
				</Button>
			</div>

			<Separator />

			{/* Category Filter */}
			<FilterGroup
				title='Categoría'
				options={categoryOptions}
				selectedValues={filters.category || []}
				onChange={values =>
					updateFilter('category', values as typeof filters.category)
				}
			/>

			<Separator />

			{/* Transmission Filter */}
			<FilterGroup
				title='Transmisión'
				options={transmissionOptions}
				selectedValues={filters.transmission || []}
				onChange={values =>
					updateFilter('transmission', values as typeof filters.transmission)
				}
			/>

			<Separator />

			{/* Fuel Type Filter */}
			<FilterGroup
				title='Tipo de Combustible'
				options={fuelTypeOptions}
				selectedValues={filters.fuelType || []}
				onChange={values =>
					updateFilter('fuelType', values as typeof filters.fuelType)
				}
			/>

			<Separator />

			{/* Seats Filter */}
			<FilterGroup
				title='Asientos'
				options={seatsOptions}
				selectedValues={(filters.seats || []).map(String)}
				onChange={values => updateFilter('seats', values.map(Number))}
			/>
		</div>
	)
}
